import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { famousPlacesApi } from '../services/api';
import PlaceCard from '../components/common/PlaceCard';
import DesignTokens from '../styles/DesignTokens';
import GlassmorphismTokens from '../styles/GlassmorphismTokens';
import SEO from '../components/seo/SEO';
import { generateCollectionPageSchema } from '../utils/structuredData';

const PageContainer = styled.div`
  padding-top: 80px;
  background: #000c1a;
  background-image: url(${GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  min-height: 100vh;
  padding-bottom: 100px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.scenicOverlay};
    z-index: 0;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${props => props.theme.breakpoints?.tablet || '768px'}) {
    padding-top: 0;
    padding-bottom: 80px;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    padding-bottom: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const ScenicHeaderBand = styled.div`
  background-image: url(${GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  padding: 2rem 1rem 1.5rem;
  position: relative;
  margin-bottom: 1.5rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.scenicOverlay};
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @supports not (backdrop-filter: blur(1px)) {
    background-image: none;
    background: #1e293b;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 1.5rem 1rem 1rem;
  }
`;

const ScenicHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  padding: 0;
`;

const PageTitle = styled.h1`
  font-size: ${DesignTokens.typography.fontSize['3xl']};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  line-height: ${DesignTokens.typography.lineHeight.tight};
  color: white;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${DesignTokens.typography.fontSize['2xl']};
  }
`;

const ResultsCount = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  line-height: ${DesignTokens.typography.lineHeight.normal};
  color: rgba(255, 255, 255, 0.7);
`;

const SearchBar = styled.form`
  position: relative;
  max-width: 600px;
  margin: 1.5rem 0 0;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: ${GlassmorphismTokens.blur.sm};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.sm};
  border: ${GlassmorphismTokens.border.light};
  border-radius: ${props => props.theme.borderRadius.full};
  padding: 0.75rem 1.5rem;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.3);
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: ${DesignTokens.typography.fontSize.base};
    color: white;
    outline: none;

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  button {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;
    }

    &:hover {
      color: white;
    }
  }
`;

const FiltersSection = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const FilterButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${props => props.active ? DesignTokens.typography.fontWeight.semibold : DesignTokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const ActiveFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${props => props.theme.colors.primaryLight};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.primary};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PlacesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%;
  align-items: start;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  & > * {
    min-width: 0;
    max-width: 100%;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem 0;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.disabled ? props.theme.colors.surface : 'white'};
  color: ${props => props.disabled ? props.theme.colors.textTertiary : props.theme.colors.text};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const PageInfo = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${DesignTokens.typography.fontSize.sm};
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  font-size: ${DesignTokens.typography.fontSize.sm};
  margin-bottom: 1.5rem;
`;

function PlacesPage({ saved = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || '';
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
  const [selectedDifficulty, setSelectedDifficulty] = useState(searchParams.get('difficulty') || '');
  const [localSearchQuery, setLocalSearchQuery] = useState(searchParams.get('search') || '');
  const searchQuery = (searchParams.get('search') || '').trim();

  const [currentPage, setCurrentPage] = useState(0);
  
  // Always fetch from famous places (50 places) - heritage destinations are in separate tab
  const { data: famousPlacesResponse, isLoading: isLoadingFamous, error } = useQuery(
    ['famousPlaces', selectedCategory, searchQuery],
    () => famousPlacesApi.getFamousPlaces(),
    {
      retry: 1,
      staleTime: 5 * 60 * 1000,
    }
  );
  
  // Use only famous places (50 places) - heritage destinations are separate
  const allFamousPlaces = useMemo(() => {
    return famousPlacesResponse?.data || [];
  }, [famousPlacesResponse?.data]);
  
  // Filter places based on category and search
  const places = useMemo(() => {
    let filtered = allFamousPlaces;
    
    // Filter by category if selected
    if (selectedCategory) {
      filtered = filtered.filter(p => (p.category || 'other') === selectedCategory);
    }
    
    // Filter by search query if provided
    if (searchQuery) {
      filtered = filtered.filter(place => 
        place.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        place.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        place.city?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [allFamousPlaces, selectedCategory, searchQuery]);
  
  const totalItems = places.length;
  const totalPages = 1; // All 50 places fit on one page
  const isLoadingData = isLoadingFamous;

  const categories = [...new Set(places.map(p => p.category).filter(Boolean))].sort();
  const countries = [...new Set(places.map(p => p.country).filter(Boolean))].sort();

  const getImageCount = (place) => {
    let count = 0;
    if (place.imageUrl) count++;
    if (place.images && Array.isArray(place.images)) {
      count += place.images.length;
      if (place.imageUrl && place.images.includes(place.imageUrl)) {
        count--;
      }
    }
    return count;
  };

  const filteredPlaces = useMemo(() => {
    // Apply country and difficulty filters (search already applied in places)
    const filtered = places.filter(place => {
      const matchesCountry = !selectedCountry || place.country === selectedCountry;
      const matchesDifficulty = !selectedDifficulty || place.difficultyLevel?.toString() === selectedDifficulty;

      return matchesCountry && matchesDifficulty;
    });
    
    return filtered.sort((a, b) => {
      const imageCountA = getImageCount(a);
      const imageCountB = getImageCount(b);
      return imageCountB - imageCountA;
    });
  }, [places, selectedCountry, selectedDifficulty]);

  const activeFilters = [
    selectedCategory && { key: 'category', label: selectedCategory, value: selectedCategory },
    selectedCountry && { key: 'country', label: selectedCountry, value: selectedCountry },
    selectedDifficulty && { key: 'difficulty', label: `Level ${selectedDifficulty}`, value: selectedDifficulty }
  ].filter(Boolean);

  const clearFilter = (key) => {
    if (key === 'category') {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('category');
        return newParams;
      });
    }
    if (key === 'country') setSelectedCountry('');
    if (key === 'difficulty') setSelectedDifficulty('');
    setCurrentPage(0);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(key);
      return newParams;
    });
  };

  const clearAllFilters = () => {
    setSelectedCountry('');
    setSelectedDifficulty('');
    setLocalSearchQuery('');
    setCurrentPage(0);
    setSearchParams({});
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (localSearchQuery.trim()) {
        newParams.set('search', localSearchQuery.trim());
      } else {
        newParams.delete('search');
      }
      return newParams;
    });
  };

  const getPageTitle = () => {
    if (saved) return 'Saved Places';
    if (searchQuery) return `Search Results for "${searchQuery}"`;
    if (selectedCategory) return `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Destinations`;
    if (selectedCountry) return `Destinations in ${selectedCountry}`;
    return 'Explore Destinations';
  };

  const getPageDescription = () => {
    if (saved) return `Your saved places on Safar Glimpse.`;
    if (searchQuery) {
      return `Find places matching "${searchQuery}". Discover ${filteredPlaces.length} heritage sites and destinations.`;
    }
    if (selectedCategory) {
      return `Explore ${filteredPlaces.length} ${selectedCategory.toLowerCase()} places. Discover heritage sites around the world.`;
    }
    if (selectedCountry) {
      return `Browse ${filteredPlaces.length} places in ${selectedCountry}. Your guide to heritage sites and destinations.`;
    }
    return `Browse our collection of ${filteredPlaces.length} heritage sites and famous destinations. Explore UNESCO World Heritage Sites and other remarkable places.`;
  };

  const collectionSchema = filteredPlaces.length > 0 
    ? generateCollectionPageSchema(filteredPlaces, getPageTitle(), getPageDescription())
    : null;

  return (
    <PageContainer>
      <SEO
        title={getPageTitle()}
        description={getPageDescription()}
        image="/social-preview.png"
        structuredData={collectionSchema ? [collectionSchema] : null}
      />

      <ScenicHeaderBand>
        <ScenicHeaderContent>
          <PageHeader>
            <PageTitle>{saved ? 'Saved Places' : 'Explore Destinations'}</PageTitle>
            <ResultsCount>
              {isLoadingData ? 'Loading...' : `${totalItems.toLocaleString()} destination${totalItems !== 1 ? 's' : ''} found`}
            </ResultsCount>

            {!saved && (
              <SearchBar onSubmit={handleSearch}>
                <MagnifyingGlassIcon style={{ width: '20px', height: '20px', color: 'rgba(255,255,255,0.5)' }} />
                <input
                  type="text"
                  placeholder="Search for places"
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  aria-label="Search for places"
                />
                <button type="submit">Search</button>
              </SearchBar>
            )}
          </PageHeader>
        </ScenicHeaderContent>
      </ScenicHeaderBand>

      <Container>
        {!saved && (
          <>
            <FiltersSection>
              <FilterButton
                active={!selectedCategory && !selectedCountry && !selectedDifficulty}
                onClick={() => {
                  setSelectedCountry('');
                  setSelectedDifficulty('');
                  setCurrentPage(0);
                  setSearchParams({});
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <AdjustmentsHorizontalIcon />
                All
              </FilterButton>

              {categories.map(category => (
                <FilterButton
                  key={category}
                  active={selectedCategory === category}
                  onClick={() => {
                    setCurrentPage(0);
                    setSearchParams(prev => {
                      const newParams = new URLSearchParams(prev);
                      if (selectedCategory === category) {
                        newParams.delete('category');
                      } else {
                        newParams.set('category', category);
                      }
                      return newParams;
                    });
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
                </FilterButton>
              ))}

              {countries.slice(0, 5).map(country => (
                <FilterButton
                  key={country}
                  active={selectedCountry === country}
                  onClick={() => {
                    setSelectedCountry(selectedCountry === country ? '' : country);
                    setCurrentPage(0);
                    setSearchParams(prev => {
                      const newParams = new URLSearchParams(prev);
                      if (selectedCountry === country) {
                        newParams.delete('country');
                      } else {
                        newParams.set('country', country);
                      }
                      return newParams;
                    });
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {country}
                </FilterButton>
              ))}
            </FiltersSection>

            {activeFilters.length > 0 && (
              <ActiveFilters>
                {activeFilters.map(filter => (
                  <ActiveFilter key={filter.key}>
                    <span>{filter.label}</span>
                    <ClearButton onClick={() => clearFilter(filter.key)}>
                      <XMarkIcon />
                    </ClearButton>
                  </ActiveFilter>
                ))}
                {activeFilters.length > 1 && (
                  <ActiveFilter>
                    <ClearButton onClick={clearAllFilters} style={{ padding: '0.5rem 0.75rem' }}>
                      Clear all
                    </ClearButton>
                  </ActiveFilter>
                )}
              </ActiveFilters>
            )}
          </>
        )}

        {isLoadingData ? (
          <LoadingState>
            <div className="loading-spinner"></div>
            <span style={{ marginLeft: '1rem' }}>Loading destinations...</span>
          </LoadingState>
        ) : error ? (
          <EmptyState>
            <EmptyStateTitle>Unable to load destinations</EmptyStateTitle>
            <EmptyStateText>Please try again later.</EmptyStateText>
          </EmptyState>
        ) : filteredPlaces.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>No destinations found</EmptyStateTitle>
            <EmptyStateText>Try adjusting your search or filter options.</EmptyStateText>
          </EmptyState>
        ) : (
          <>
            <PlacesGrid>
              {filteredPlaces.map((place, index) => (
                <PlaceCard key={place.id} place={place} index={index} source="places" />
              ))}
            </PlacesGrid>
            
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0 || isLoadingData}
                >
                  Previous
                </PaginationButton>
                <PageInfo>
                  Page {currentPage + 1} of {totalPages}
                </PageInfo>
                <PaginationButton
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1 || isLoadingData}
                >
                  Next
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        )}
      </Container>
    </PageContainer>
  );
}

export default PlacesPage;
